import { PlatformID } from '@common/clients/api';
import { type ResolvedContextData } from '@common/defaults/ContextData';
import { type SidebarData, WidgetType } from '@common/types/Sidebar';

import { Widget as LatestPodcasts } from './LatestPodcasts';
import { Widget as MostCommented } from './MostCommented';
import { Widget as MostLikedComments } from './MostLikedComments';
import { Widget as MostRead } from './MostRead';
import { Widget as MostViewed } from './MostViewed';
import { NewsPopular } from './NewsPopular';
import { Widget as ProNews } from './ProNews';
import { Widget as RecentNews } from './RecentNews';
import { Widget as TransferRumour } from './TransferRumour';

export interface Props {
    contextData: ResolvedContextData;
    widgetType: WidgetType;
    sidebarProps: SidebarData;
    adIndex?: number;
}

const DEFAULT_GROUPED_WIDGETS = [WidgetType.MOST_READ, WidgetType.MOST_COMMENTED];
const VI_GROUPED_WIDGET = [WidgetType.MOST_READ, WidgetType.PRO_NEWS];

const checkIfWidgetTabDataIsPresent = (
    widgetGroups: WidgetType[],
    sidebarProps: Omit<SidebarData, 'sequence'>,
) => {
    return widgetGroups.every(
        (widgetKey: WidgetType) => widgetKey in sidebarProps && sidebarProps[widgetKey],
    );
};

// FIXME: PB-6012 - Handle (1) Pro uitgelicht widget data-fetching and layout in sidebar
export const SideWidget = ({
    contextData,
    widgetType,
    sidebarProps: { sequence, ...widgetProps },
    adIndex,
}: Props) => {
    const platformID = contextData.platform.id;
    const widgetGroups = platformID === PlatformID.VI ? VI_GROUPED_WIDGET : DEFAULT_GROUPED_WIDGETS;
    const hasTabbedWidgetGroups = checkIfWidgetTabDataIsPresent(widgetGroups, widgetProps);

    if (hasTabbedWidgetGroups && widgetGroups.includes(widgetType)) {
        if (platformID === PlatformID.VI) {
            return (
                <NewsPopular
                    contextData={contextData}
                    dataList={{
                        [WidgetType.MOST_READ]: widgetProps['most-read'] || [],
                        [WidgetType.PRO_NEWS]: widgetProps['pro-news'] || [],
                    }}
                />
            );
        }
        return (
            <NewsPopular
                contextData={contextData}
                dataList={{
                    [WidgetType.MOST_READ]: widgetProps['most-read'] || [],
                    [WidgetType.MOST_COMMENTED]: widgetProps['most-commented'] || [],
                }}
            />
        );
    }

    switch (widgetType) {
        case WidgetType.MOST_READ:
            return <MostRead contextData={contextData} dataList={widgetProps['most-read']} />;
        case WidgetType.PRO_NEWS:
            return <ProNews contextData={contextData} dataList={widgetProps['pro-news']} />;
        case WidgetType.MOST_COMMENTED:
            return <MostCommented contextData={contextData} dataList={widgetProps['most-commented']} />;
        case WidgetType.LATEST_PODCASTS:
            return <LatestPodcasts contextData={contextData} dataList={widgetProps['latest-podcasts']} />;
        case WidgetType.RECENT_NEWS:
            return (
                <RecentNews
                    contextData={contextData}
                    adIndex={adIndex}
                    dataList={widgetProps['recent-news']}
                />
            );
        case WidgetType.TRANSFER_RUMOUR:
            return <TransferRumour contextData={contextData} dataList={widgetProps['transfer-rumour']} />;
        case WidgetType.MOST_VIEWED:
            return <MostViewed contextData={contextData} dataList={widgetProps['most-viewed']} />;
        case WidgetType.MOST_LIKED_COMMENTS:
            return (
                <MostLikedComments contextData={contextData} dataList={widgetProps['most-liked-comments']} />
            );
        default:
            return null;
    }
};
